import React, { FC, useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Edit, Undo } from '@mui/icons-material';
import { InputAdornment, Tooltip, TextField, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import { useUpdateApplicationManagementMutation } from 'reduxState/store/applicationManagement/api';
import { ConfirmEdit } from './ConfirmEdit';
import { ApplicationManagement } from '../../../reduxState/store/applicationManagement/types';
import DropdownSearchSelect, { IDropdownSelectOption } from 'components/DropdownSearchSelect/DropdownSearchSelect';
import { countryCodeOptions, groupedCountryCodeOptions } from 'constants/countryCodes';
import '../scss/AppName.scss';

export interface AppDefaultCountryProps {
  applicationId: string;
  Country: string;
  applicationManagement: ApplicationManagement | null;
}

export const AppDefaultCountry: FC<React.PropsWithChildren<AppDefaultCountryProps>> = ({
  applicationId,
  Country,
  applicationManagement,
}) => {
  const { user } = useAuth0();
  const [isDisabled, setDisabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [defaultCountry, setDefaultCountry] = useState<IDropdownSelectOption | null>(null);
  const [updateApplicationManagement] = useUpdateApplicationManagementMutation();
  
  const defaultCountryOptions = useMemo(
    () => [...groupedCountryCodeOptions, ...countryCodeOptions],
    []
  );

  // Find the dropdown option that matches the given country code
  const findCountryOption = useCallback(
    (countryCode: string): IDropdownSelectOption | null => {
      if (!countryCode) return null;
      return defaultCountryOptions.find(option => option.value === countryCode) || null;
    },
    [defaultCountryOptions]
  );

  // Find the option that matches the current country value
  useEffect(() => {
    setDefaultCountry(findCountryOption(Country));
  }, [Country, findCountryOption]);

  const saveDefaultCountry = async (): Promise<boolean> => {
    if (!applicationManagement) return false;
    const countryValue = defaultCountry?.value || '';
    const author = user?.email || 'unknown-user';
    
    setIsSaving(true);
    try {
      await updateApplicationManagement({
        appId: applicationId,
        applicationManagement: {
          Name: applicationManagement.Name,
          ProductPageURL: applicationManagement.ProductPageURL,
          Options: applicationManagement.Options,
          Country: countryValue,
          ServiceableCountries: applicationManagement.ServiceableCountries,
        },
        author,
      }).unwrap();
      
      toast.success('Default Country updated successfully');
      return true;
    } catch (error) {
      toast.error('Failed to update Default Country. Please try again.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const toggleDisabled = (): void => setDisabled(prevState => !prevState);

  const handleChange = (option: IDropdownSelectOption | null): void => {
    setDefaultCountry(option);
  };

  const cancelEdit = (): void => {
    // Reset to original value
    setDefaultCountry(findCountryOption(Country));
    setDisabled(true);
  };

  const handleSave = async (): Promise<void> => {
    const success = await saveDefaultCountry();
    if (success) {
      toggleDisabled();
    }
  };

  // When disabled, show as read-only text field with edit button
  if (isDisabled) {
    const displayValue = defaultCountry?.label || Country || '';
    return (
      <div className="application-name settings-form-group">
        <TextField
          id="defaultCountry"
          className="application-name-input"
          label="Default Country"
          value={displayValue}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Edit Default Country">
                  <IconButton
                    onClick={toggleDisabled}
                    aria-label="Edit Default Country"
                    data-testid="edit-button"
                    size="small"
                  >
                    <Edit className="edit-icon" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          disabled={true}
          data-testid="default-country-textbox"
        />
      </div>
    );
  }

  // When editing, show the dropdown
  return (
    <div className="application-name settings-form-group">
      <div style={{ position: 'relative', width: '100%' }}>
        <DropdownSearchSelect
          label="Default Country"
          value={defaultCountry}
          options={defaultCountryOptions}
          placeholder="Select Default Country"
          noOptionsText="No Country Found"
          handleChange={handleChange}
          isDisabled={false}
          groupBy={(option: IDropdownSelectOption) => option.group || ''}
        />
        <div
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
          }}
        >
          <Tooltip title="Cancel editing">
            <IconButton
              onClick={cancelEdit}
              aria-label="Cancel editing"
              data-testid="undo-button"
              size="small"
              disabled={isSaving}
            >
              <Undo className="edit-icon-undo" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      <ConfirmEdit
        headerText={'Edit Default Country'}
        confirmText={`Confirm save to update default country:`}
        changeText={defaultCountry?.label || ''}
        onSave={handleSave}
      />
      {isSaving && <div style={{ marginTop: '8px', color: '#666' }}>Saving...</div>}
    </div>
  );
};
